import { useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { Row, Col } from 'react-bootstrap';
import useAsync from 'react-use/lib/useAsync';
import useField from 'hooks/useField';
import { isPipelineObject } from 'components/Modals/utilities';
import FieldService from 'services/FieldService';
import PipelineService from 'services/PipelineService';
import CustomObjectsService from 'services/CustomObjectsService';
import Select from 'components/Inputs/Select';
import Input from 'components/Kizen/Input';
import { Text } from 'app/typography';
import { useTranslation } from 'react-i18next';
import InputControl from 'components/Inputs/InputControl';
import Switch from 'components/Kizen/Switch';
import { getDataQAForInput } from 'components/Inputs/helpers';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';

const Wrapper = styled.div`
  margin-bottom: 15px;
`;

export const SwitchControl = styled(InputControl)`
  > label {
    line-height: 1.2;
    margin-top: -3px;
    margin-bottom: 10px;
    & + * {
      margin: 0;
      label {
        // Incidentally part of <Switch />
        margin: 0;
      }
    }
  }
`;
const TitleWrapper = styled.div`
  display: flex;
  top: 4px;
  padding-bottom: 6px;
  p + p {
    margin-top: 0;
  }
`;
const SuppressTitleWrapper = styled.div`
  padding-bottom: 8px;
`;

const SuppressStyledRow = styled(Row)`
  margin-bottom: 19px;
`;

const StyledRow = styled(Row)`
  margin-bottom: ${({ extraPadding }) => (extraPadding ? 25 : 20)}px !important;
  :last-child {
    margin-bottom: 0 !important;
    p {
      margin-bottom: 0;
    }
  }
`;
const StyledEntityRow = styled(Row)`
  margin-bottom: 29px;
`;
const objectToOption = (object) => ({
  value: object.id,
  label: 'objectName' in object ? object.objectName : object.name,
  name: object.name,
});

const categoryToOption = (category) => ({
  value: category.id,
  label: category.name,
});

const relationTypes = (t) => [
  {
    value: 'one_to_one',
    label: t('One-to-One'),
  },
  {
    value: 'primary_for',
    label: t('One-to-Many'),
  },
  {
    value: 'primary',
    label: t('Many-to-One'),
  },
  {
    value: 'additional',
    label: t('Many-to-Many'),
  },
  // just for supporting old cardinality,
  // only show in disabled dropdown
  {
    value: 'additional_for',
    label: t('Many-to-Many'),
  },
];
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

export default function RelationshipOptions({
  updateField,
  updateValidation,
  formData = EMPTY_OBJECT,
  data = EMPTY_OBJECT,
}) {
  const { t } = useTranslation();
  const currentRelation = (formData && formData.relation) || {};
  const {
    id: relationId,
    relatedObject,
    relatedCategory,
    relatedName,
    relationType: relationTypeProp,
    rollupTimeline: rollupTimelineProp = false,
    inverseRelationRollupTimeline: inverseRelationRollupTimelineProp = false,
    rollupLeadsources: rollupLeadsourcesProp = false,
    inverseRelationRollupLeadsources:
      inverseRelationRollupLeadsourcesProp = false,
    inverseRelationSuppressed: inverseRelationSuppressedProp = false,
  } = currentRelation;

  const originInverseRelationSuppressedProp = useRef(
    inverseRelationSuppressedProp
  );
  const {
    model: { objectName },
  } = data;

  const [relatedEntityId, setRelatedEntityId] = useField(relatedObject);
  const [relationType, setRelationType] = useField(
    relationTypeProp || 'additional'
  );
  const [categoryId, setCategoryId] = useField(relatedCategory);
  const [fieldName, setFieldName] = useField(relatedName);

  const [rollupTimeline, setRollupTimeline] = useField(rollupTimelineProp);
  const [inverseRelationRollupTimeline, setInverseRelationRollupTimeline] =
    useField(inverseRelationRollupTimelineProp);
  const [rollupLeadsources, setRollupLeadsources] = useField(
    rollupLeadsourcesProp
  );
  const [
    inverseRelationRollupLeadsources,
    setInverseRelationRollupLeadsources,
  ] = useField(inverseRelationRollupLeadsourcesProp);
  const [inverseRelationSuppressed, setInverseRelationSuppressed] = useField(
    inverseRelationSuppressedProp
  );

  const isValid = Boolean(relatedEntityId && fieldName?.trim() && categoryId);

  // Load objects on mount; account-specific, independent of formData,
  // shouldn't need to be reloaded
  const { loading, value: objects = EMPTY_ARRAY } = useAsync(async () => {
    return CustomObjectsService.getAllowedRelations();
  }, []);

  const selectedModel = objects.find((model) => model.id === relatedEntityId);

  const { value: categories = EMPTY_ARRAY } = useAsync(async () => {
    try {
      if (relatedEntityId) {
        if (selectedModel.name === 'client_client') {
          return await FieldService.getObjectCategories();
        }
        return isPipelineObject(selectedModel)
          ? await PipelineService.getObjectCategories(relatedEntityId)
          : await CustomObjectsService.getObjectCategories(relatedEntityId);
      }
      return [];
    } catch {
      return [];
    }
  }, [objects, relatedEntityId]);

  useEffect(() => {
    updateValidation(isValid);
  }, [isValid, updateValidation]);

  useEffect(() => {
    updateField('relation', {
      relatedObject: relatedEntityId,
      relatedCategory: categoryId,
      relatedName: fieldName,
      relationType: relationType,
      rollupTimeline: rollupTimeline,
      inverseRelationRollupTimeline: inverseRelationRollupTimeline,
      rollupLeadsources: rollupLeadsources,
      inverseRelationRollupLeadsources: inverseRelationRollupLeadsources,
      inverseRelationSuppressed: inverseRelationSuppressed,
      ...(data.field?.id
        ? { cardinality: data.field.relation?.cardinality }
        : { cardinality: 'many_to_many' }),
      ...(relationId && {
        id: relationId,
      }),
    });
  }, [
    relationId,
    rollupTimeline,
    inverseRelationRollupTimeline,
    relatedEntityId,
    categoryId,
    relationType,
    fieldName,
    updateField,
    data.field,
    inverseRelationSuppressed,
    rollupLeadsources,
    inverseRelationRollupLeadsources,
  ]);
  const isRelationSuppressed = useMemo(() => {
    return (
      inverseRelationSuppressed || originInverseRelationSuppressedProp.current
    );
  }, [inverseRelationSuppressed]);

  if (loading) {
    return null;
  }

  const selectedCategory = categories.find((cat) => cat.id === categoryId);
  return (
    <Wrapper data-qa="related_object_subsection">
      <TitleWrapper>
        <TextEllipsisWithTooltip weight="bold">
          {`${objectName}`}
        </TextEllipsisWithTooltip>
        &nbsp;
        <Text weight="bold">{t('Field')}</Text>
      </TitleWrapper>
      <StyledEntityRow>
        <Col>
          <InputControl
            label={t('Related Object')}
            {...getDataQAForInput('related_object', 'dropdown')}
          >
            <Select
              fullWidth
              placeholder={t('Choose Related Object')}
              value={selectedModel ? objectToOption(selectedModel) : null}
              options={objects.map(objectToOption)}
              onChange={(opt) => {
                setRelatedEntityId(opt.value);
                setCategoryId(null); // Reset category selection
              }}
              disabled={!!data.field}
            />
          </InputControl>
        </Col>
        <Col>
          {relatedEntityId ? (
            <InputControl
              label={t('Relationship Type')}
              {...getDataQAForInput('relationship_type', 'dropdown')}
            >
              <Select
                fullWidth
                value={relationType}
                options={
                  data.field ? relationTypes(t) : relationTypes(t).slice(0, -1)
                }
                onChange={(opt) => {
                  setRelationType(opt.value);
                }}
                disabled={!!data.field}
              />
            </InputControl>
          ) : null}
        </Col>
      </StyledEntityRow>
      {relatedEntityId && (
        <>
          <SuppressTitleWrapper>
            <Text weight="bold">{t('Related Field')}</Text>
          </SuppressTitleWrapper>
          <SuppressStyledRow>
            <Col>
              <SwitchControl
                label={t('Suppress Related Field')}
                labelInfo={t(
                  "When suppressed, the related object will not have a relationship field visible, nor will updates to this relationship appear in that object's record timelines. This is not reversible."
                )}
                {...getDataQAForInput('inverse_relation_suppressed')}
              >
                <Switch
                  checked={inverseRelationSuppressed}
                  onChange={(e) => {
                    setInverseRelationSuppressed(e.target.checked);
                    if (e.target.checked) {
                      setRollupTimeline(false);
                      setInverseRelationRollupTimeline(false);
                      setRollupLeadsources(false);
                      setInverseRelationRollupLeadsources(false);
                    }
                  }}
                  disabled={
                    originInverseRelationSuppressedProp.current ||
                    !!data.field?.isSuppressed
                  }
                />
              </SwitchControl>
            </Col>
          </SuppressStyledRow>
          <StyledRow>
            <Col>
              <InputControl label={t('Field Name')}>
                <Input
                  placeholder={t('Enter Field Name')}
                  value={fieldName}
                  onChange={setFieldName}
                  {...getDataQAForInput('related_field_name', 'text')}
                />
              </InputControl>
            </Col>
            <Col>
              <InputControl
                label={t('Category to Create Related Field')}
                {...getDataQAForInput('related_field_category', 'dropdown')}
              >
                <Select
                  fullWidth
                  placeholder={t('Choose Category')}
                  value={
                    selectedCategory ? categoryToOption(selectedCategory) : null
                  }
                  options={categories.map(categoryToOption)}
                  onChange={(opt) => setCategoryId(opt.value)}
                />
              </InputControl>
            </Col>
          </StyledRow>
          <StyledRow extraPadding>
            <Col>
              <SwitchControl
                label={t('Share Timeline To Related?')}
                labelInfo={t(
                  "Timeline entries will be shared from this object's record to the associated related object's record."
                )}
                {...getDataQAForInput('share_timeline_to_related')}
              >
                <Switch
                  checked={inverseRelationRollupTimeline}
                  disabled={isRelationSuppressed || !!data.field?.isSuppressed}
                  onChange={(e) =>
                    setInverseRelationRollupTimeline(e.target.checked)
                  }
                />
              </SwitchControl>
            </Col>
            <Col>
              <SwitchControl
                label={t('Share Timeline From Related?')}
                labelInfo={t(
                  "Timeline entries will be shared from the related object's record and displayed on associated records of this object."
                )}
                {...getDataQAForInput('share_timeline_from_related')}
              >
                <Switch
                  checked={rollupTimeline}
                  disabled={isRelationSuppressed || !!data.field?.isSuppressed}
                  onChange={(e) => setRollupTimeline(e.target.checked)}
                />
              </SwitchControl>
            </Col>
          </StyledRow>
          <StyledRow extraPadding>
            <Col>
              <SwitchControl
                label={t('Share Lead Sources To Related?')}
                labelInfo={t(
                  "Lead Sources will be shared from this object's record to the associated related object's record."
                )}
                {...getDataQAForInput('share_leadsources_to_related')}
              >
                <Switch
                  checked={inverseRelationRollupLeadsources}
                  disabled={isRelationSuppressed || !!data.field?.isSuppressed}
                  onChange={(e) =>
                    setInverseRelationRollupLeadsources(e.target.checked)
                  }
                />
              </SwitchControl>
            </Col>
            <Col>
              <SwitchControl
                label={t('Share Lead Sources From Related?')}
                labelInfo={t(
                  "Lead Sources will be shared from the related object's record and displayed on associated records of this object."
                )}
                {...getDataQAForInput('share_leadspurces_from_related')}
              >
                <Switch
                  checked={rollupLeadsources}
                  disabled={isRelationSuppressed || !!data.field?.isSuppressed}
                  onChange={(e) => setRollupLeadsources(e.target.checked)}
                />
              </SwitchControl>
            </Col>
          </StyledRow>
        </>
      )}
    </Wrapper>
  );
}
